import "./ClientPersonalHabitsFormComponent.scss";
import CardComponent from "../../../../shared/components/card/CardComponent";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {Misc, Patterns} from "../../../../constants";
import FormikInputComponent from "../../../../shared/components/form-controls/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import {IClientPersonalHabitsForm} from "../../../../shared/models/client.model";
import {useDispatch} from "react-redux";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";
import HorizontalLineV2Component from "../../../../shared/components/horizontal-line-v2/HorizontalLineV2Component";
import AcknowledgementFormComponent from "../../acknowledgement-form/AcknowledmentFormComponent";
import ModalComponent from "../../../../shared/components/modal/ModalComponent";

interface ClientPersonalHabitsFormComponentProps {
    onCancel: () => void;
    onNext?: () => void;
    clientId: string;
    onSave: (clientPersonalHabits: any) => void;
    clientMedicalHistoryDetails: any;
    isClientMedicalHistoryDetailsLoaded: boolean | undefined;
    isClientMedicalHistoryDetailsLoading: boolean | undefined;
    isClientMedicalHistoryDetailsLoadingFailed: boolean | undefined;
}

const ClientPersonalHabitsFormValidationSchema = Yup.object({
    personal_habits: Yup.object({
        "Smoke/Chew Tobacco?": Yup.object({
            value: Yup.string().required('Smoke/Chew Tobacco is required'),
            text: Yup.string().when("value", {
                is: "Yes",
                then: Yup.string().required('Smoke/Chew Tobacco is required')
            })
        }),
        "Drink Alcohol?": Yup.object({
            value: Yup.string().required('Drink Alcohol is required'),
            text: Yup.string().when("value", {
                is: "Yes",
                then: Yup.string().required('Drink Alcohol is required')
            })
        }),
        "Drink Coffee?": Yup.object({
            value: Yup.string().required('Drink Coffee is required'),
            text: Yup.string().when("value", {
                is: "Yes",
                then: Yup.string().required('Drink Coffee is required')
            })
        }),
        "Drink Soda/Pop?": Yup.object({
            value: Yup.string().required('Drink Soda/Pop is required'),
            text: Yup.string().when("value", {
                is: "Yes",
                then: Yup.string().required('Drink Soda/Pop is required')
            })
        }),
    }),
});

const ClientPersonalHabitsFormInitialValues: IClientPersonalHabitsForm = {
    "personal_habits": {
        "Smoke/Chew Tobacco?": {
            "value": "",
            "text": ""
        },
        "Drink Alcohol?": {
            "value": ""
        },
        "Drink Coffee?": {
            "value": "",
            "text": ""
        },
        "Drink Soda/Pop?": {
            "value": ""
        }
    }
};

const FormQuestions = [
    {
        key: "Smoke/Chew Tobacco?",
        title: "Smoke/Chew Tobacco?",
        placeholder: "Cigarettes/day",
        id: "smoke"
    },
    {
        key: "Drink Alcohol?",
        title: "Drink Alcohol?",
        placeholder: "Drinks/day",
        id: "alcohol"
    },
    {
        key: "Drink Coffee?",
        title: "Drink Coffee?",
        placeholder: "Cups/day",
        id: "coffee"
    },
    {
        key: "Drink Soda/Pop?",
        title: "Drink Soda/Pop?",
        placeholder: "Cups/day",
        id: "soda"
    }
]

const ClientPersonalHabitsFormComponent = (props: ClientPersonalHabitsFormComponentProps) => {

    const {
        onCancel,
        onSave,
        clientId,
        clientMedicalHistoryDetails,
        isClientMedicalHistoryDetailsLoaded,
        isClientMedicalHistoryDetailsLoading,
        isClientMedicalHistoryDetailsLoadingFailed
    } = props;
    const [clientPersonalHabitsFormInitialValues, setClientPersonalHabitsFormInitialValues] = useState<IClientPersonalHabitsForm>(_.cloneDeep(ClientPersonalHabitsFormInitialValues));
    const [isClientPersonalHabitsSavingInProgress, setIsClientPersonalHabitsSavingInProgress] = useState(false);
    const dispatch = useDispatch();
    const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);


    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        setClientPersonalHabitsFormInitialValues(values);
        setIsAcknowledgementModalOpen(true);
    }, [])

    const handleAcknowledgementFormSubmit = useCallback((acknowledgementValues: any) => {
        const payload = {
            ...clientPersonalHabitsFormInitialValues,
            ...acknowledgementValues,
            client_id: clientId,
            update_type: "profile",
        };
        setIsClientPersonalHabitsSavingInProgress(true);
        CommonService._client.ClientMedicalHistoryQuestionnairePersonalDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<IClientPersonalHabitsForm>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setIsClientPersonalHabitsSavingInProgress(false);
                setClientPersonalHabitsFormInitialValues(_.cloneDeep(clientPersonalHabitsFormInitialValues));
                onSave(response.data);
            })
            .catch((error: IAPIResponseType<any>) => {
                CommonService._alert.showToast(error[Misc.API_RESPONSE_MESSAGE_KEY], 'error');
                setIsClientPersonalHabitsSavingInProgress(false);
            })
    }, [onSave, clientId,clientPersonalHabitsFormInitialValues]);

    useEffect(() => {
        if (clientMedicalHistoryDetails) {
            setClientPersonalHabitsFormInitialValues({
                personal_habits: clientMedicalHistoryDetails?.personal_habits
            });
        }
    }, [dispatch, clientMedicalHistoryDetails]);

    return (
        <div className={'client-personal-habits-form-component'}>
            <>
                {
                    isClientMedicalHistoryDetailsLoading && <div>
                        <LoaderComponent/>
                    </div>
                }
                {
                    isClientMedicalHistoryDetailsLoadingFailed &&
                    <StatusCardComponent title={"Failed to fetch medical Details"}/>
                }
            </>
            {isClientMedicalHistoryDetailsLoaded &&
                <>
                    <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                                    title={"Back"}/>

                    <Formik
                        validationSchema={ClientPersonalHabitsFormValidationSchema}
                        initialValues={clientPersonalHabitsFormInitialValues}
                        onSubmit={onSubmit}
                        validateOnChange={false}
                        validateOnBlur={true}
                        enableReinitialize={true}
                        validateOnMount={true}>
                        {({values, setFieldValue, isValid, validateForm}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (
                                <CardComponent>
                                    <div className={'edit-personal-habits-heading'}>
                                        Edit Personal Habits
                                    </div>
                                    <Form noValidate={true} className={"t-form form-question-list"}>

                                        <DetailsViewCardComponent
                                            title={"Personal Habits"}>
                                            <>
                                                <div className={'edit-personal-habits-form-sub-heading'}>
                                                    Have you ever or do you currently:
                                                </div>
                                                {
                                                    FormQuestions.map((question: any, index) => {
                                                        const {key, title, placeholder} = question;
                                                        return <>
                                                            <div className="ts-row ts-align-items-center form-question"
                                                                 key={key}>
                                                                <div className="ts-col">
                                                                    {title}
                                                                </div>
                                                                <div className="ts-col mrg-top-25">
                                                                    <Field name={`personal_habits.${key}.value`}>
                                                                        {
                                                                            (field: FieldProps) => (
                                                                                <FormikRadioButtonGroupComponent
                                                                                    options={CommonService._staticData.yesNoOptions}
                                                                                    displayWith={(option) => option.title}
                                                                                    valueExtractor={(option) => option.title}
                                                                                    required={true}
                                                                                    formikField={field}
                                                                                    id={`radio`}
                                                                                    onChange={(value) => {
                                                                                        if (value === "No") {
                                                                                            setFieldValue(`personal_habits.${key}.text`, undefined);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            )
                                                                        }
                                                                    </Field>
                                                                </div>
                                                                <div className="ts-col text-area-wrapper">
                                                                    {
                                                                        // @ts-ignore
                                                                        values.personal_habits[key]?.value === 'Yes' &&
                                                                        <div
                                                                            className="ts-row d-flex ts-align-items-baseline">
                                                                            <div className="ts-col-6">
                                                                                <Field
                                                                                    name={`personal_habits.${key}.text`}>
                                                                                    {
                                                                                        (field: FieldProps) => (
                                                                                            <FormikInputComponent
                                                                                                placeholder={placeholder}
                                                                                                type={"text"}
                                                                                                validationPattern={Patterns.POSITIVE_DECIMAL_NUMBER_UPTO_TWO_DIGIT}
                                                                                                required={true}
                                                                                                formikField={field}
                                                                                                size={"small"}
                                                                                                fullWidth={true}

                                                                                            />
                                                                                        )
                                                                                    }
                                                                                </Field>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </div>
                                                                <div className="ts-col-2"/>

                                                            </div>
                                                            {question !== FormQuestions[FormQuestions.length - 1] &&
                                                                <HorizontalLineV2Component/>}
                                                        </>
                                                    })
                                                }
                                            </>
                                        </DetailsViewCardComponent>
                                        <div className="t-form-actions mrg-bottom-0">
                                            <ButtonComponent
                                                id={"home_btn"}
                                                variant={"outlined"}
                                                onClick={onCancel}
                                                disabled={isClientPersonalHabitsSavingInProgress}
                                                color={'accent'}
                                            >
                                                Cancel
                                            </ButtonComponent>&nbsp;
                                            <ButtonComponent
                                                id={"save_next_btn"}
                                                className={'mrg-left-25'}
                                                isLoading={isClientPersonalHabitsSavingInProgress}
                                                disabled={isClientPersonalHabitsSavingInProgress || !isValid }
                                                type={"submit"}
                                                color={'accent'}
                                            >
                                                {/*{isClientPersonalHabitsSavingInProgress ? "Saving" : "Save"}*/}
                                                Next
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                </CardComponent>
                            )
                        }}
                    </Formik>
                    <ModalComponent isOpen={isAcknowledgementModalOpen}>
                        <AcknowledgementFormComponent setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
                                                      isLoading={isClientPersonalHabitsSavingInProgress}
                                                      onSubmit={handleAcknowledgementFormSubmit}/>
                    </ModalComponent>
                </>
            }
        </div>
    );

};

export default ClientPersonalHabitsFormComponent;
